import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmpresaSidebar } from "@/components/empresa/EmpresaSidebar";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, User, MapPin, Phone, Linkedin, Instagram, Briefcase, GraduationCap, Languages, Award, FileText, ChevronLeft, CheckCircle, XCircle, Clock, Calendar, Users, Accessibility } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, parseISO, differenceInYears } from "date-fns";
import { ptBR } from "date-fns/locale";
type Application = {
  id: string;
  candidate_id: string;
  status: string;
  applied_at: string;
  candidate: {
    id: string;
    user_id: string;
    full_name: string;
    phone: string | null;
    cpf: string | null;
    city: string | null;
    state: string | null;
    bio: string | null;
    linkedin_url: string | null;
    github_url: string | null;
    desired_area: string | null;
    desired_job_type: string | null;
    desired_work_model: string | null;
    salary_expectation_min: number | null;
    salary_expectation_max: number | null;
    is_pcd: boolean | null;
    availability: string | null;
    soft_skills: string[] | null;
    gender: string | null;
    birth_date: string | null;
    desired_positions: string[] | null;
    marital_status: string | null;
    has_children_under_18: boolean | null;
  };
};
type Experience = {
  id: string;
  company_name: string;
  role: string;
  start_date: string;
  end_date: string | null;
  description: string | null;
};
type Education = {
  id: string;
  course: string;
  institution: string;
  start_date: string;
  end_date: string | null;
};
type Skill = {
  id: string;
  skill_name: string;
  level: string | null;
};
type Language = {
  id: string;
  language: string;
  level: string;
};
type Course = {
  id: string;
  course_name: string;
  institution: string;
  start_date: string;
  end_date: string | null;
  workload_hours: number | null;
};
type Question = {
  id: string;
  question_text: string;
  question_type: string;
};
type Answer = {
  id: string;
  question_id: string;
  answer_text: string;
};
const statusLabels: Record<string, {
  label: string;
  color: string;
}> = {
  enviado: {
    label: "Enviado",
    color: "bg-blue-500"
  },
  "em análise": {
    label: "Em análise",
    color: "bg-yellow-500"
  },
  aprovado: {
    label: "Aprovado",
    color: "bg-green-500"
  },
  rejeitado: {
    label: "Rejeitado",
    color: "bg-red-500"
  }
};
const formatPhone = (value: string | null | undefined): string => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
  }
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
};
const EmpresaVagaCandidatosPage = () => {
  const {
    jobId
  } = useParams<{
    jobId: string;
  }>();
  const navigate = useNavigate();
  const {
    loading: accessLoading,
    companyId
  } = useCompanyAccess(true);
  const [loading, setLoading] = useState(true);
  const [jobTitle, setJobTitle] = useState("");
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);

  // Candidate details
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [education, setEducation] = useState<Education[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch job and applications
  useEffect(() => {
    if (!companyId || !jobId) return;
    const fetchData = async () => {
      setLoading(true);

      // Fetch job title
      const {
        data: jobData
      } = await supabase.from("jobs").select("title, company_id").eq("id", jobId).maybeSingle();
      if (!jobData || jobData.company_id !== companyId) {
        toast.error("Vaga não encontrada");
        navigate("/empresa/dashboard");
        return;
      }
      setJobTitle(jobData.title);

      // Fetch applications with candidate data
      const {
        data: appsData
      } = await supabase.from("applications").select(`
          id,
          candidate_id,
          status,
          applied_at,
          candidate:candidate_profiles!applications_candidate_id_fkey (
            id,
            user_id,
            full_name,
            phone,
            cpf,
            city,
            state,
            bio,
            linkedin_url,
            github_url,
            desired_area,
            desired_job_type,
            desired_work_model,
            salary_expectation_min,
            salary_expectation_max,
            is_pcd,
            availability,
            soft_skills,
            gender,
            birth_date,
            desired_positions,
            marital_status,
            has_children_under_18
          )
        `).eq("job_id", jobId).order("applied_at", {
        ascending: false
      });
      if (appsData) {
        const formattedApps = appsData.map((app: any) => ({
          ...app,
          candidate: app.candidate
        }));
        setApplications(formattedApps);
      }

      // Fetch job questions
      const {
        data: questionsData
      } = await supabase.from("job_questions").select("id, question_text, question_type").eq("job_id", jobId).order("order_index");
      if (questionsData) {
        setQuestions(questionsData);
      }
      setLoading(false);
    };
    fetchData();
  }, [companyId, jobId, navigate]);

  // Fetch candidate details when selected
  useEffect(() => {
    if (!selectedApplication) return;
    const fetchCandidateDetails = async () => {
      setLoadingDetails(true);
      const candidateId = selectedApplication.candidate_id;
      const [expRes, eduRes, skillsRes, langRes, answersRes, coursesRes] = await Promise.all([supabase.from("candidate_experiences").select("*").eq("candidate_id", candidateId).order("start_date", {
        ascending: false
      }), supabase.from("candidate_education").select("*").eq("candidate_id", candidateId).order("start_date", {
        ascending: false
      }), supabase.from("candidate_skills").select("*").eq("candidate_id", candidateId), supabase.from("candidate_languages").select("*").eq("candidate_id", candidateId), supabase.from("application_answers").select("*").eq("application_id", selectedApplication.id), supabase.from("candidate_courses").select("*").eq("candidate_id", candidateId).order("start_date", {
        ascending: false
      })]);
      setExperiences(expRes.data || []);
      setEducation(eduRes.data || []);
      setSkills(skillsRes.data || []);
      setLanguages(langRes.data || []);
      setAnswers(answersRes.data || []);
      setCourses(coursesRes.data || []);
      setLoadingDetails(false);
    };
    fetchCandidateDetails();
  }, [selectedApplication]);
  const handleStatusChange = async (newStatus: string) => {
    if (!selectedApplication) return;
    const {
      error
    } = await supabase.from("applications").update({
      status: newStatus as "enviado" | "em análise" | "aprovado" | "rejeitado"
    }).eq("id", selectedApplication.id);
    if (error) {
      toast.error("Erro ao atualizar status");
      return;
    }
    setApplications(prev => prev.map(app => app.id === selectedApplication.id ? {
      ...app,
      status: newStatus
    } : app));
    setSelectedApplication(prev => prev ? {
      ...prev,
      status: newStatus
    } : null);
    toast.success("Status atualizado");
  };
  const handleSelectCandidate = (app: Application) => {
    setSelectedApplication(app);
    // Open sheet on mobile
    if (window.innerWidth < 768) {
      setSheetOpen(true);
    }
  };
  const filteredApplications = applications.filter(app => filterStatus === "all" || app.status === filterStatus);
  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy", {
        locale: ptBR
      });
    } catch {
      return dateStr;
    }
  };
  const calculateAge = (birthDate: string) => {
    try {
      return differenceInYears(new Date(), parseISO(birthDate));
    } catch {
      return null;
    }
  };
  const formatGender = (gender: string | null) => {
    if (!gender) return null;
    const genderMap: Record<string, string> = {
      masculino: "Masculino",
      feminino: "Feminino",
      outro: "Outro",
      prefiro_nao_informar: "Prefere não informar"
    };
    return genderMap[gender.toLowerCase()] || gender;
  };
  const formatMaritalStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      'solteiro': 'Solteiro(a)',
      'casado': 'Casado(a)',
      'divorciado': 'Divorciado(a)',
      'viuvo': 'Viúvo(a)',
      'uniao_estavel': 'União Estável'
    };
    return statusMap[status] || status;
  };
  const formatCpf = (cpf: string): string => {
    const digits = cpf.replace(/\D/g, '');
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };
  const TimelineItem = ({
    title,
    subtitle,
    period,
    description,
    isLast
  }: {
    title: string;
    subtitle: string;
    period: string;
    description?: string | null;
    isLast?: boolean;
  }) => <div className="relative pl-6">
      {/* Linha vertical */}
      {!isLast && <div className="absolute left-[7px] top-3 h-full w-[2px] bg-border" />}
      {/* Ponto do timeline */}
      <div className="absolute left-0 top-1.5 h-4 w-4 rounded-full border-2 border-primary bg-background" />
      {/* Conteúdo */}
      <div className="pb-6">
        <p className="font-medium text-sm">{title}</p>
        <p className="text-muted-foreground text-sm">{subtitle}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{period}</p>
        {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
      </div>
    </div>;

  // Candidate Details Content - reusable for both desktop and mobile
  const CandidateDetailsContent = () => {
    if (!selectedApplication) return null;
    if (loadingDetails) {
      return <div className="flex-1 flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>;
    }
    return <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-xl font-bold">{selectedApplication.candidate?.full_name}</h2>
            {/* Personal Info Row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
              {selectedApplication.candidate?.phone && <span className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {formatPhone(selectedApplication.candidate.phone)}
                </span>}
              {selectedApplication.candidate?.city && <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {selectedApplication.candidate.city}
                  {selectedApplication.candidate.state && `, ${selectedApplication.candidate.state}`}
                </span>}
              {selectedApplication.candidate?.gender && <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {formatGender(selectedApplication.candidate.gender)}
                </span>}
              {selectedApplication.candidate?.birth_date && <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {calculateAge(selectedApplication.candidate.birth_date)} anos
                </span>}
            </div>
            {/* Links Row */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
              {selectedApplication.candidate?.linkedin_url && <a href={selectedApplication.candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                  <Linkedin className="h-3.5 w-3.5" />
                  LinkedIn
                </a>}
              {selectedApplication.candidate?.github_url && <a href={selectedApplication.candidate.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-pink-500 hover:underline">
                  <Instagram className="h-3.5 w-3.5" />
                  Instagram
                </a>}
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={selectedApplication.status === "em análise" ? "default" : "outline"} onClick={() => handleStatusChange("em análise")} className="flex-1 sm:flex-none">
              <Clock className="h-4 w-4 mr-1" />
              <span className="text-xs sm:text-sm">Em análise</span>
            </Button>
            <Button size="sm" variant={selectedApplication.status === "aprovado" ? "default" : "outline"} className={`flex-1 sm:flex-none ${selectedApplication.status === "aprovado" ? "bg-green-600 hover:bg-green-700" : ""}`} onClick={() => handleStatusChange("aprovado")}>
              <CheckCircle className="h-4 w-4 mr-1" />
              <span className="text-xs sm:text-sm">Aprovar</span>
            </Button>
            <Button size="sm" variant={selectedApplication.status === "rejeitado" ? "default" : "outline"} className={`flex-1 sm:flex-none ${selectedApplication.status === "rejeitado" ? "bg-red-600 hover:bg-red-700" : ""}`} onClick={() => handleStatusChange("rejeitado")}>
              <XCircle className="h-4 w-4 mr-1" />
              <span className="text-xs sm:text-sm">Rejeitar</span>
            </Button>
          </div>
        </div>

        <Separator />

        {/* Bio */}
        {selectedApplication.candidate?.bio && <>
            <div>
              <h3 className="font-semibold mb-2">Sobre</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {selectedApplication.candidate.bio}
              </p>
            </div>
            <Separator />
          </>}

        {/* Professional Objectives */}
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Objetivos Profissionais
          </h3>
          {(() => {
          const hasObjectives = selectedApplication.candidate?.desired_area || selectedApplication.candidate?.desired_job_type || selectedApplication.candidate?.desired_work_model || selectedApplication.candidate?.salary_expectation_min || selectedApplication.candidate?.salary_expectation_max || selectedApplication.candidate?.desired_positions && selectedApplication.candidate.desired_positions.length > 0;
          if (!hasObjectives) {
            return <p className="text-sm text-muted-foreground">
                  Nenhum objetivo profissional cadastrado
                </p>;
          }
          return <>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedApplication.candidate?.desired_area && <div>
                      <p className="text-muted-foreground">Área desejada</p>
                      <p className="font-medium">{selectedApplication.candidate.desired_area}</p>
                    </div>}
                  {selectedApplication.candidate?.desired_job_type && <div>
                      <p className="text-muted-foreground">Tipo de vaga</p>
                      <p className="font-medium">{selectedApplication.candidate.desired_job_type}</p>
                    </div>}
                  {selectedApplication.candidate?.desired_work_model && <div>
                      <p className="text-muted-foreground">Modelo de trabalho</p>
                      <p className="font-medium">{selectedApplication.candidate.desired_work_model}</p>
                    </div>}
                  {(selectedApplication.candidate?.salary_expectation_min || selectedApplication.candidate?.salary_expectation_max) && <div>
                      <p className="text-muted-foreground">Pretensão salarial</p>
                      <p className="font-medium">
                        {selectedApplication.candidate.salary_expectation_min && selectedApplication.candidate.salary_expectation_max ? `R$ ${selectedApplication.candidate.salary_expectation_min.toLocaleString()} - R$ ${selectedApplication.candidate.salary_expectation_max.toLocaleString()}` : selectedApplication.candidate.salary_expectation_min ? `A partir de R$ ${selectedApplication.candidate.salary_expectation_min.toLocaleString()}` : `Até R$ ${selectedApplication.candidate.salary_expectation_max?.toLocaleString()}`}
                      </p>
                    </div>}
                </div>
                {/* Cargos Pretendidos */}
                {selectedApplication.candidate?.desired_positions && selectedApplication.candidate.desired_positions.length > 0 && <div className="mt-4">
                    <p className="text-muted-foreground text-sm mb-2">Cargos pretendidos</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedApplication.candidate.desired_positions.map((pos, idx) => <Badge key={idx} variant="secondary">{pos}</Badge>)}
                    </div>
                  </div>}
              </>;
        })()}
        </div>

        <Separator />

        {/* Experiences */}
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Experiências
          </h3>
          {experiences.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma experiência cadastrada</p> : <div className="ml-2">
              {experiences.map((exp, index) => <TimelineItem key={exp.id} title={exp.role} subtitle={exp.company_name} period={`${formatDate(exp.start_date)} - ${exp.end_date ? formatDate(exp.end_date) : "Atual"}`} description={exp.description} isLast={index === experiences.length - 1} />)}
            </div>}
        </div>

        <Separator />

        {/* Cursos - BEFORE Formação */}
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Award className="h-4 w-4" />
            Cursos
          </h3>
          {courses.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum curso cadastrado</p> : <div className="ml-2">
              {courses.map((course, index) => <TimelineItem key={course.id} title={course.course_name} subtitle={course.institution} period={`${formatDate(course.start_date)} - ${course.end_date ? formatDate(course.end_date) : "Em andamento"}${course.workload_hours ? ` • ${course.workload_hours}h` : ""}`} isLast={index === courses.length - 1} />)}
            </div>}
        </div>

        <Separator />

        {/* Education - AFTER Cursos */}
        <div>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            Formação
          </h3>
          {education.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma formação cadastrada</p> : <div className="ml-2">
              {education.map((edu, index) => <TimelineItem key={edu.id} title={edu.course} subtitle={edu.institution} period={`${formatDate(edu.start_date)} - ${edu.end_date ? formatDate(edu.end_date) : "Cursando"}`} isLast={index === education.length - 1} />)}
            </div>}
        </div>

        <Separator />

        {/* Skills */}
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Award className="h-4 w-4" />
            Habilidades
          </h3>
          {skills.length === 0 && (!selectedApplication.candidate?.soft_skills || selectedApplication.candidate.soft_skills.length === 0) ? <p className="text-sm text-muted-foreground">Nenhuma habilidade cadastrada</p> : <div className="space-y-3">
              {skills.length > 0 && <div>
                  <p className="text-xs text-muted-foreground mb-2">Competências técnicas</p>
                  <div className="flex flex-wrap gap-2">
                    {skills.map(skill => <Badge key={skill.id} variant="secondary">
                        {skill.skill_name}
                        {skill.level && ` • ${skill.level}`}
                      </Badge>)}
                  </div>
                </div>}
              {selectedApplication.candidate?.soft_skills && selectedApplication.candidate.soft_skills.length > 0 && <div>
                  <p className="text-xs text-muted-foreground mb-2">Competências comportamentais</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedApplication.candidate.soft_skills.map((skill, idx) => <Badge key={idx} variant="outline">
                        {skill}
                      </Badge>)}
                  </div>
                </div>}
            </div>}
        </div>

        <Separator />

        {/* Languages */}
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Languages className="h-4 w-4" />
            Idiomas
          </h3>
          {languages.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum idioma cadastrado</p> : <div className="flex flex-wrap gap-2">
              {languages.map(lang => <Badge key={lang.id} variant="secondary">
                  {lang.language} • {lang.level}
                </Badge>)}
            </div>}
        </div>

        {/* Custom Questions & Answers */}
        {questions.length > 0 && answers.length > 0 && <>
            <Separator />
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Respostas do Formulário
              </h3>
              <div className="space-y-3">
                {questions.map(q => {
              const answer = answers.find(a => a.question_id === q.id);
              return <div key={q.id} className="text-sm">
                      <p className="font-medium">{q.question_text}</p>
                      <p className="text-muted-foreground mt-1">
                        {answer?.answer_text || "Não respondida"}
                      </p>
                    </div>;
            })}
              </div>
            </div>
          </>}

        {/* Dados Adicionais */}
        <Separator />
        <div>
          <h3 className="font-semibold mb-3">Dados Adicionais</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {selectedApplication.candidate?.marital_status && <div>
                <p className="text-muted-foreground">Estado civil</p>
                <p className="font-medium">{formatMaritalStatus(selectedApplication.candidate.marital_status)}</p>
              </div>}
            {selectedApplication.candidate?.has_children_under_18 !== null && <div>
                <p className="text-muted-foreground">Filhos menores de 18 anos</p>
                <p className="font-medium">{selectedApplication.candidate.has_children_under_18 ? "Sim" : "Não"}</p>
              </div>}
            {selectedApplication.candidate?.is_pcd && <div className="flex items-center gap-2">
                <Accessibility className="h-4 w-4 text-primary" />
                <span>Pessoa com Deficiência (PCD)</span>
              </div>}
            {selectedApplication.candidate?.availability && <div>
                <p className="text-muted-foreground">Disponibilidade</p>
                <p className="font-medium">{selectedApplication.candidate.availability}</p>
              </div>}
          </div>
        </div>
      </div>;
  };
  if (accessLoading || loading) {
    return <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center bg-muted/30">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>;
  }
  return <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 flex">
        <EmpresaSidebar />
      <main className="flex-1 p-4 md:p-6 bg-muted/30 overflow-hidden">
          <div className="h-full flex flex-col mobile-container max-w-7xl md:mx-auto">
            {/* Header - with padding for mobile sidebar button */}
            <div className="mb-4 lg:pl-0 pt-10 md:pt-0 flex-col gap-0 flex items-start justify-between py-0 px-0 pl-0">
              <div>
                <h1 className="text-2xl font-display font-bold">Candidatos</h1>
                <p className="text-muted-foreground">
                  {filteredApplications.length} candidato{filteredApplications.length !== 1 ? "s" : ""} para {jobTitle}
                </p>
              </div>
            </div>

            {/* Mobile Layout - Full width list */}
            <div className="md:hidden flex-1 flex flex-col overflow-hidden">
              <Card className="flex-1 flex flex-col overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">
                      Candidatos ({filteredApplications.length})
                    </CardTitle>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="enviado">Enviado</SelectItem>
                        <SelectItem value="em análise">Em análise</SelectItem>
                        <SelectItem value="aprovado">Aprovado</SelectItem>
                        <SelectItem value="rejeitado">Rejeitado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <ScrollArea className="flex-1">
                  <div className="px-4 pb-4 space-y-3 overflow-hidden">
                    {filteredApplications.length === 0 ? <div className="text-center py-8 text-muted-foreground">
                        <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhum candidato encontrado</p>
                      </div> : filteredApplications.map(app => <Card key={app.id} onClick={() => handleSelectCandidate(app)} className={`p-4 cursor-pointer transition-all ${selectedApplication?.id === app.id ? "ring-2 ring-primary bg-primary/5" : "hover:shadow-md"}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-base truncate">
                                {app.candidate?.full_name || "Nome não disponível"}
                              </p>
                              {app.candidate?.city && <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                  <MapPin className="h-3.5 w-3.5" />
                                  {app.candidate.city}
                                  {app.candidate.state && `, ${app.candidate.state}`}
                                </p>}
                              <p className="text-sm text-muted-foreground mt-1">
                                {formatDate(app.applied_at)}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              <Badge className={`${statusLabels[app.status]?.color || "bg-gray-500"} text-white text-xs`}>
                                {statusLabels[app.status]?.label || app.status}
                              </Badge>
                              {app.candidate?.is_pcd && <Badge variant="outline" className="text-xs">PCD</Badge>}
                            </div>
                          </div>
                        </Card>)}
                  </div>
                </ScrollArea>
              </Card>

              {/* Sheet for candidate details on mobile */}
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetContent side="right" className="w-full sm:w-[95vw] p-0 overflow-y-auto">
                  <SheetHeader className="p-4 border-b sticky top-0 bg-background z-10">
                    <div className="flex items-center gap-3">
                      <Button variant="ghost" size="icon" onClick={() => setSheetOpen(false)}>
                        <ChevronLeft className="h-5 w-5" />
                      </Button>
                      <SheetTitle className="text-left">Detalhes do Candidato</SheetTitle>
                    </div>
                  </SheetHeader>
                  <div className="p-4">
                    {selectedApplication && !loadingDetails ? <CandidateDetailsContent /> : loadingDetails ? <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div> : null}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop Layout - Split View */}
            <div className="hidden md:flex flex-1 gap-4 overflow-hidden">
              {/* Left Panel - Candidates List (45%) */}
              <Card className="w-[45%] lg:w-2/5 flex flex-col overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">
                      Candidatos ({filteredApplications.length})
                    </CardTitle>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="enviado">Enviado</SelectItem>
                        <SelectItem value="em análise">Em análise</SelectItem>
                        <SelectItem value="aprovado">Aprovado</SelectItem>
                        <SelectItem value="rejeitado">Rejeitado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <ScrollArea className="flex-1">
                  <div className="px-4 pb-4 space-y-2 overflow-hidden">
                    {filteredApplications.length === 0 ? <div className="text-center py-8 text-muted-foreground">
                        <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Nenhum candidato encontrado</p>
                      </div> : filteredApplications.map(app => <div key={app.id} onClick={() => handleSelectCandidate(app)} className={`p-3 rounded-lg cursor-pointer transition-colors border ${selectedApplication?.id === app.id ? "bg-primary/10 border-primary" : "hover:bg-muted border-transparent"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate">
                                {app.candidate?.full_name || "Nome não disponível"}
                              </p>
                              {app.candidate?.city && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                  <MapPin className="h-3 w-3" />
                                  {app.candidate.city}
                                  {app.candidate.state && `, ${app.candidate.state}`}
                                </p>}
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(app.applied_at)}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0">
                              <Badge className={`${statusLabels[app.status]?.color || "bg-gray-500"} text-white text-xs`}>
                                {statusLabels[app.status]?.label || app.status}
                              </Badge>
                              {app.candidate?.is_pcd && <Badge variant="outline" className="text-xs">PCD</Badge>}
                            </div>
                          </div>
                        </div>)}
                  </div>
                </ScrollArea>
              </Card>

              {/* Right Panel - Candidate Details (55%) */}
              <Card className="w-[55%] lg:w-3/5 flex flex-col overflow-hidden">
                {!selectedApplication ? <div className="flex-1 flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
                      <p>Selecione um candidato para ver o currículo</p>
                    </div>
                  </div> : loadingDetails ? <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div> : <ScrollArea className="flex-1">
                    <div className="p-6">
                      <CandidateDetailsContent />
                    </div>
                  </ScrollArea>}
              </Card>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>;
};
export default EmpresaVagaCandidatosPage;